import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
} from "@mui/material";

import {
    Add,
    Delete,
    Edit,
} from "@mui/icons-material";

import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { imageBaseurl } from "../utils/common";

const initialForm = {
    match_id: "",
    slug: "",
    short_title: "",
    description: "",
    category: "",
    country: "",
    language: "",
    logo: "",
    banner: "",
    is_live: false,
    is_featured: false,
    status: true,
    servers: [
        {
            server_name: "",
            stream_url: "",
            stream_type: "url",
            quality: "HD",
            is_default: true,
            status: true,
        },
    ],
};

export default function MatchDetails() {
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] =
        useState(false);

    const [deleteId, setDeleteId] =
        useState(null);
    const [open, setOpen] = useState(false);

    const [formData, setFormData] =
        useState(initialForm);

    const [editId, setEditId] = useState(null);

    const [search, setSearch] = useState("");

    const handleImageUpload = (
        e,
        fieldName
    ) => {

        const file =
            e.target.files[0];

        if (!file) return;

        setFormData((prev) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        try {

            await apiClient.delete(
                `/api/admin/match/${deleteId}`
            );

            toast.success(
                "Match deleted successfully"
            );

            getChannels();

            setDeleteDialog(false);

            setDeleteId(null);

        } catch (error) {

            console.log(error);

            toast.error(
                "Delete failed"
            );
        }
    };
    const getChannels = async () => {
        try {
            setLoading(true);

            const res = await apiClient.get(
                "/api/admin/match"
            );

            setChannels(res.data.data || []);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getChannels();
    }, []);

    // ============================
    // HANDLE INPUT
    // ============================
    const handleChange = (e) => {
        const { name, value, checked, type } =
            e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : value,
        }));
    };

    // ============================
    // SERVER CHANGE
    // ============================
    const handleServerChange = (
        index,
        field,
        value
    ) => {
        const updatedServers = [
            ...formData.servers,
        ];

        updatedServers[index][field] = value;

        setFormData({
            ...formData,
            servers: updatedServers,
        });
    };

    // ============================
    // ADD SERVER
    // ============================
    const addServer = () => {
        setFormData({
            ...formData,
            servers: [
                ...formData.servers,
                {
                    server_name: "",
                    stream_url: "",
                    stream_type: "url",
                    quality: "HD",
                    is_default: false,
                    status: true,
                },
            ],
        });
    };

    // ============================
    // REMOVE SERVER
    // ============================
    const removeServer = (index) => {
        const updatedServers =
            formData.servers.filter(
                (_, i) => i !== index
            );

        setFormData({
            ...formData,
            servers: updatedServers,
        });
    };

    // ============================
    // SUBMIT
    // ============================
    const handleSubmit = async () => {
        // =========================
        // BASIC VALIDATION
        // =========================

        if (!formData.match_id?.trim()) {
            return toast.error(
                "Match Id is required"
            );
        }

       
        if (
            !formData.servers ||
            formData.servers.length === 0
        ) {
            return toast.error(
                "At least one server is required"
            );
        }

        for (
            let i = 0;
            i < formData.servers.length;
            i++
        ) {
            const server =
                formData.servers[i];

            if (
                !server.server_name?.trim()
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Server name is required`
                );
            }

            if (
                !server.stream_url?.trim()
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Stream URL is required`
                );
            }

            // URL VALIDATION
            const urlPattern =
                /^(https?:\/\/)/i;

            if (
                !urlPattern.test(
                    server.stream_url
                )
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Invalid stream URL`
                );
            }

            if (
                !server.stream_type
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Stream type is required`
                );
            }
        }

        try {
           
            if (editId) {

                await apiClient.put(
                    `/api/admin/match/${editId}`,
                    formData,
                   
                );

            } else {
                await apiClient.post(
                    "/api/admin/match",
                    formData,
                );
            }

            getChannels();

            setOpen(false);

            setFormData(initialForm);

            setEditId(null);
        } catch (error) {
             toast.error(error?.response?.data?.message
            );
            console.log(error);
        }
    };

    // ============================
    // EDIT
    // ============================
    const handleEdit = (item) => {
        setFormData({ ...item, servers: JSON.parse(item?.servers) });
        setEditId(item.id);
        setOpen(true);
    };

    // ============================
    // FILTER
    // ============================
    const filteredChannels =
        channels.filter((item) =>
            item.match_id
                ?.toLowerCase()
                .includes(search.toLowerCase())
        );

    return (
        <Box p={3}>
            <Card>
                <CardContent>
                    {/* HEADER */}
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        justifyContent="space-between"
                        spacing={2}
                        mb={3}
                    >
                        <Typography variant="h5">
                           Match Management
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <TextField
                                size="small"
                                placeholder="Search Match Id"
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                            />

                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    setOpen(true);
                                    setEditId(null);
                                    setFormData(initialForm);
                                }}
                            >
                                Add Match
                            </Button>
                        </Stack>
                    </Stack>

                    {/* TABLE */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Match Id
                                    </TableCell>

                                    {/* <TableCell>
                                        Category
                                    </TableCell> */}

                                    <TableCell>
                                        Servers
                                    </TableCell>

                                    {/* <TableCell>
                                        Live
                                    </TableCell> */}

                                    <TableCell>
                                        Status
                                    </TableCell>

                                    <TableCell>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredChannels.map(
                                    (item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    alignItems="center"
                                                >
                                                    {/* <img
                                                        src={imageBaseurl + item.logo}
                                                        alt=""
                                                        width={50}
                                                        height={50}
                                                        style={{
                                                            borderRadius: 10,
                                                            objectFit:
                                                                "cover",
                                                        }}
                                                    /> */}

                                                    <Box>
                                                        <Typography
                                                            fontWeight={700}
                                                        >
                                                            {
                                                                item.match_id
                                                            }
                                                        </Typography>

                                                        {/* <Typography
                                                            variant="body2"
                                                        >
                                                            {item.country}
                                                        </Typography> */}
                                                    </Box>
                                                </Stack>
                                            </TableCell>

                                            {/* <TableCell>
                                                <Chip
                                                    label={
                                                        item.category
                                                    }
                                                />
                                            </TableCell> */}

                                            <TableCell>
                                                {
                                                    JSON.parse(item.servers)
                                                        ?.length
                                                }
                                            </TableCell>

                                            {/* <TableCell>
                                                <Chip
                                                    color={
                                                        item.is_live
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    label={
                                                        item.is_live
                                                            ? "Live"
                                                            : "Offline"
                                                    }
                                                />
                                            </TableCell> */}

                                            <TableCell>
                                                <Chip
                                                    color={
                                                        item.status
                                                            ? "success"
                                                            : "error"
                                                    }
                                                    label={
                                                        item.status
                                                            ? "Active"
                                                            : "Inactive"
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleEdit(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        <Edit />
                                                    </IconButton>

                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteClick(
                                                                item.id
                                                            )
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* DIALOG */}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    {editId
                        ? "Edit Match"
                        : "Add Match"}
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} mt={1}>

                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Match Id"
                                name="match_id"
                                value={
                                    formData.match_id
                                }
                                onChange={handleChange}
                            />
                        </Grid>
                        {/* <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Short Title"
                                name="short_title"
                                value={formData.short_title}
                                onChange={handleChange}
                            />
                        </Grid> */}
                       
                        <Grid item size={{ xs: 12, md: 12 }}>
                            <Stack
                                direction="row"
                                spacing={4}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography>
                                        Status
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.status
                                        }
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                status:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </Stack>
                            </Stack>
                        </Grid>
                       

                        {/* SERVERS */}

                        <Grid item size={{ xs: 12 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                mb={2}
                            >
                                <Typography variant="h6">
                                    Servers
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={addServer}
                                >
                                    Add Server
                                </Button>
                            </Stack>

                            {formData.servers.map(
                                (server, index) => (
                                    <Card
                                        key={index}
                                        sx={{ mb: 2 }}
                                    >
                                        <CardContent>
                                            <Grid
                                                container
                                                spacing={2}
                                            >
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={3}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label="Server Name"
                                                        value={
                                                            server.server_name
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "server_name",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>

                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 4 }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label="Stream URL"
                                                        value={
                                                            server.stream_url
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "stream_url",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>

                                                 <Grid
                                                    item
                                                    size={{ xs: 12, md: 2 }}
                                                >
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        label="Type"
                                                        value={
                                                            server.stream_type
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "stream_type",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value="url">
                                                            Url
                                                        </MenuItem>

                                                        <MenuItem value="iframe">
                                                            Iframe
                                                        </MenuItem>

                                                        {/* <MenuItem value="youtube">
                                                            Youtube
                                                        </MenuItem> */}
                                                    </TextField>
                                                </Grid>

                                                
                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 1 }}
                                                >
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            removeServer(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )
                            )}
                        </Grid>

                      
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            setOpen(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        {editId
                            ? "Update"
                            : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteDialog}
                onClose={() =>
                    setDeleteDialog(false)
                }
            >

                <DialogTitle>
                    Delete Match
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Are you sure you want
                        to delete this Match?
                    </Typography>
                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setDeleteDialog(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDelete}
                    >
                        Delete
                    </Button>

                </DialogActions>

            </Dialog>
        </Box>
    );
}